/* Small pure helpers for the Admin "Ticket rules" tab. No React, no network.
 * The SERVER is the authority on what a valid setting is (api/src/shared/adminSettings.js); these only
 * keep the form tidy and say, in plain words, why a save did not come back as saved. */

export const WEEKDAYS = [
  { n: 1, label: 'Mon' }, { n: 2, label: 'Tue' }, { n: 3, label: 'Wed' }, { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' }, { n: 6, label: 'Sat' }, { n: 0, label: 'Sun' },
];

/** 7 -> "07:00". */
export const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;

/** Turn one working day on or off. The list stays sorted, with no repeats. */
export function toggleDay(days, n) {
  const next = days.includes(n) ? days.filter((d) => d !== n) : [...days, n];
  return [...new Set(next)].sort((a, b) => a - b);
}

function isRealDate(text) {
  if (typeof text !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
  const d = new Date(`${text}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === text;
}

/** Add one holiday. Answers { ok: true, list } (sorted) or { ok: false, message }. */
export function addHoliday(list, text) {
  const date = String(text || '').trim();
  if (!isRealDate(date)) return { ok: false, message: 'That is not a real date. Pick a day from the calendar.' };
  if (list.includes(date)) return { ok: false, message: 'That day is already on the list.' };
  return { ok: true, list: [...list, date].sort() };
}

export const removeHoliday = (list, date) => list.filter((d) => d !== date);

const sameList = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

/** Has the form changed from what the server last gave us? */
export function calendarDirty(form, saved) {
  return form.startHour !== saved.startHour || form.endHour !== saved.endHour
    || !sameList([...form.workDays].sort(), [...saved.workDays].sort())
    || !sameList([...form.holidays].sort(), [...saved.holidays].sort());
}

/** "2026-12-25" -> "Fri, Dec 25, 2026". The date is read as a calendar day, not as a moment. */
export function holidayLabel(date) {
  const d = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** The sentence for a save that did not come back as saved. */
export function wordsForSettingsError(error) {
  if (error && error.outcome === 'rejected') {
    if (error.code === 'STALE') return 'Another admin changed these settings first, so your change was NOT applied. The form now shows their values. Make your change again if you still want it.';
    if (error.code === 'NOT_READY') return 'These settings are not turned on yet.';
    if (error.status === 403) return 'Only an IT Admin can change these settings.';
    return error.message;
  }
  if (error && error.code === 'WALKTHROUGH_WRITE_BLOCKED') return error.message;
  return 'We could not confirm that this saved. Press Reload settings and look, then save again if it is not there - saving the same thing twice does no harm.';
}

/** Why the settings could not be shown. */
export function wordsForLoadError(error) {
  if (error && error.status === 403) return 'Only an IT Admin can see these settings.';
  if (error && error.status === 503) return 'These settings are not turned on yet.';
  if (error && error.status === 401) return 'These settings are only available when you are signed in to the live app.';
  // A 404 is the server not having the settings at all - not a sign-in problem (PR #115).
  if (error && error.status === 404) return 'These settings are not available on the server right now. Try again in a moment.';
  return 'The settings could not be loaded. Try again in a moment.';
}
